/**
 * O que faz: funções puras do rascunho local do assistente de organização (montar colunas, ajustar regras,
 *            exemplos fictícios e conversão para a publicação única).
 * Onde usa: ConfiguracaoOrganizacaoKanban, EditorRegraColuna e ExemplosOrganizacaoKanban.
 * Conexões: catálogo e contratos compartilhados com o worker (contratos.ts); não chama rede nem IA.
 * Tabelas: nenhuma; só a publicação (servico.ts) grava no banco.
 * Data: 21/10/2026.
 */
import {
  CATALOGO_CONDICOES,
  COLUNAS_INICIAIS,
  LIMITES_ORGANIZACAO,
  sugerirRegraLocal,
  type CondicaoColuna,
  type ConfiguracaoOrganizacao,
  type ExigenciaRegistro,
  type FrequenciaOrganizacao,
  type MarcoAnalitico,
  type RascunhoOrganizacao,
  type RegraColuna,
} from './contratos';

/** Configuração lida do servidor + rotas aprovadas (calibração vigente) para o passo de ativação. */
export interface ConfiguracaoOrganizacaoTela extends ConfiguracaoOrganizacao {
  calibradas: string[];
}

/** Coluna do Pipeline como o assistente precisa: ordem e se é protegida (venda, retorno ou evento Meta). */
export interface EtapaOrganizacao {
  id: string;
  nome: string;
  ordem: number;
  protegida: boolean;
}

/** Uma linha do rascunho: coluna existente ou sugerida e a regra ainda não publicada. */
export interface ColunaRascunho {
  etapaId: string;
  nome: string;
  // Coluna sugerida que ainda não existe; só é criada pela publicação confirmada.
  nova: boolean;
  organizar: boolean;
  regra: RegraColuna;
}

export interface EstadoAssistente {
  origem: 'sugerida' | 'existente';
  colunas: ColunaRascunho[];
  frequencia: FrequenciaOrganizacao;
  movimento: 'sugerir' | 'mover';
  // IDs das colunas em que a clínica escolheu "Mover nas colunas permitidas" (só rotas calibradas).
  automaticas: string[];
}

// Registro do CRM → marco analítico correspondente; a coluna não inventa venda/presença pela conversa.
export const MARCO_POR_REGISTRO: Record<Exclude<ExigenciaRegistro, null>, MarcoAnalitico> = {
  venda: 'convertido',
  agenda: 'agendado',
  comparecimento: 'compareceu',
};

// Frase da coluna "somente pelos registros do CRM" dizendo QUAL registro, para a clínica não ficar com "o correspondente".
const DESCRICAO_POR_REGISTRO: Record<Exclude<ExigenciaRegistro, null>, string> = {
  agenda: 'Usar somente quando houver consulta marcada no CRM.',
  comparecimento: 'Usar somente quando o comparecimento estiver registrado no CRM.',
  venda: 'Usar somente quando houver venda registrada no CRM.',
};

/** Exemplos fictícios do passo 3: frases do próprio catálogo, para não depender de conversas reais. */
export const EXEMPLOS_FICTICIOS: Array<{ texto: string; condicao: CondicaoColuna }> = [
  { texto: 'Quero entender como funciona o implante.', condicao: 'interesse' },
  { texto: 'Quanto custa e pode parcelar?', condicao: 'preco' },
  { texto: 'Podemos marcar minha avaliação na terça?', condicao: 'quer_marcar' },
];

export function normalizarNomeColuna(nome: string): string {
  return nome.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** ID provisório da coluna sugerida; o servidor troca pelo ID real na publicação. */
export function gerarIdTemporario(): string {
  return crypto.randomUUID();
}

/** Aplica a escolha de significado do catálogo; qualquer ajuste exige nova confirmação ("Está certo"). */
export function regraComCondicao(regra: RegraColuna, condicao: CondicaoColuna, exigeRegistro: ExigenciaRegistro = null): RegraColuna {
  const opcao = CATALOGO_CONDICOES[condicao];
  const registro = condicao === 'fato_sistema' ? exigeRegistro ?? regra.exigeRegistro : null;
  return {
    ...regra,
    condicao,
    // "Explicar com minhas palavras" começa vazio de propósito: a clínica descreve, o sistema não adivinha.
    descricao: condicao === 'personalizado' ? '' : registro ? DESCRICAO_POR_REGISTRO[registro] : opcao.descricao,
    exclusoes: [...opcao.exclusoes],
    exemplos: opcao.exemplos.map((exemplo) => ({ ...exemplo })),
    exigeRegistro: registro,
    marcoAnalitico: registro ? MARCO_POR_REGISTRO[registro] : null,
    confirmada: false,
    modo: 'suggestion',
  };
}

/** Regra inicial pelo nome: só nomes conhecidos recebem sugestão; os demais pedem o significado. */
export function regraInicial(etapaId: string, nome: string, prioridade: number, origens: string[]): RegraColuna {
  const base: RegraColuna = {
    etapaId, nome, condicao: 'personalizado', descricao: '', exclusoes: [], exemplos: [], prioridade,
    origensPermitidas: origens, marcoAnalitico: null, exigeRegistro: null, modo: 'suggestion', confirmada: false,
  };
  const sugestao = sugerirRegraLocal(nome);
  if (sugestao.precisaEsclarecimento || !sugestao.condicao) return base;
  return { ...regraComCondicao(base, sugestao.condicao, sugestao.exigeRegistro), etapaId, nome, prioridade, origensPermitidas: origens };
}

/**
 * Validação de tela (o servidor valida de novo): descrição dentro do limite e, em "registros do CRM",
 * o registro escolhido. Regra incompleta não pode ser confirmada nem publicada.
 */
export function regraPronta(regra: RegraColuna): boolean {
  const descricao = regra.descricao.trim();
  if (descricao.length === 0 || descricao.length > LIMITES_ORGANIZACAO.descricao) return false;
  if (regra.condicao === 'fato_sistema' && !regra.exigeRegistro) return false;
  return true;
}

/** Origens padrão: colunas de atendimento anteriores na ordem atual, sem colunas protegidas/terminais. */
function origensAnteriores(ordenadas: EtapaOrganizacao[], indice: number): string[] {
  return ordenadas.slice(0, indice).filter((etapa) => !etapa.protegida).map((etapa) => etapa.id);
}

/**
 * Monta as linhas do rascunho a partir do começo escolhido. Nada é criado aqui:
 * "sugerida" usa IDs provisórios para as colunas que ainda não existem.
 */
export function montarColunas(
  origem: 'sugerida' | 'existente',
  etapas: EtapaOrganizacao[],
  publicada: ConfiguracaoOrganizacao | null,
): ColunaRascunho[] {
  const ordenadas = [...etapas].sort((a, b) => a.ordem - b.ordem);
  const regrasPublicadas = new Map((publicada?.regras ?? []).map((regra) => [regra.etapaId, regra]));
  if (origem === 'existente') {
    const temPublicacao = regrasPublicadas.size > 0;
    return ordenadas.map((etapa, indice) => {
      const regraAtual = regrasPublicadas.get(etapa.id);
      // Regra publicada volta como estava (já confirmada); a clínica revê só o que quiser mudar.
      if (regraAtual) return { etapaId: etapa.id, nome: etapa.nome, nova: false, organizar: true, regra: { ...regraAtual, nome: etapa.nome } };
      return {
        etapaId: etapa.id,
        nome: etapa.nome,
        nova: false,
        // Primeira coluna costuma ser a entrada dos leads; colunas protegidas só entram por escolha explícita.
        organizar: !temPublicacao && indice > 0 && !etapa.protegida,
        regra: regraInicial(etapa.id, etapa.nome, indice, origensAnteriores(ordenadas, indice)),
      };
    });
  }
  const existentesNaoProtegidas = ordenadas.filter((etapa) => !etapa.protegida).map((etapa) => etapa.id);
  const novasAnteriores: string[] = [];
  return COLUNAS_INICIAIS.map((sugerida, indice) => {
    const existente = ordenadas.find((etapa) => normalizarNomeColuna(etapa.nome) === normalizarNomeColuna(sugerida.nome));
    if (existente) {
      const posicao = ordenadas.indexOf(existente);
      return {
        etapaId: existente.id, nome: existente.nome, nova: false, organizar: true,
        regra: regraInicial(existente.id, existente.nome, indice, origensAnteriores(ordenadas, posicao)),
      };
    }
    const id = gerarIdTemporario();
    // Coluna nova entra no fim do quadro: pode receber cards das colunas de atendimento e das sugeridas anteriores.
    const origens = [...existentesNaoProtegidas, ...novasAnteriores];
    novasAnteriores.push(id);
    return { etapaId: id, nome: sugerida.nome, nova: true, organizar: true, regra: regraInicial(id, sugerida.nome, indice, origens) };
  });
}

/** Destino atual de um exemplo fictício: a coluna cuja regra o marca como correspondente. */
export function destinoDoExemplo(colunas: ColunaRascunho[], texto: string): string | null {
  const coluna = colunas.find((item) => item.organizar && item.regra.exemplos.some((exemplo) => exemplo.texto === texto && exemplo.corresponde));
  return coluna?.etapaId ?? null;
}

/**
 * Troca o destino de um exemplo: tira a frase de todas as regras e a coloca como positiva no destino
 * (e como negativa na coluna anterior). Só muda o rascunho; não chama Jev, LLM nem rede.
 */
export function alterarDestinoExemplo(colunas: ColunaRascunho[], texto: string, destino: string | null): ColunaRascunho[] {
  const anterior = destinoDoExemplo(colunas, texto);
  if (anterior === destino) return colunas;
  return colunas.map((coluna) => {
    const citaFrase = coluna.regra.exemplos.some((exemplo) => exemplo.texto === texto);
    // Colunas que não citam a frase e não participam da troca ficam idênticas (mesma referência).
    if (!citaFrase && coluna.etapaId !== destino && coluna.etapaId !== anterior) return coluna;
    const semFrase = coluna.regra.exemplos.filter((exemplo) => exemplo.texto !== texto);
    let exemplos = semFrase;
    if (coluna.etapaId === destino) exemplos = [...semFrase, { texto, corresponde: true }];
    else if (coluna.etapaId === anterior) exemplos = [...semFrase, { texto, corresponde: false }];
    // Limite compartilhado com o servidor: mantém os exemplos mais recentes.
    return { ...coluna, regra: { ...coluna.regra, exemplos: exemplos.slice(-LIMITES_ORGANIZACAO.exemplos) } };
  });
}

/** Conteúdo que muda a interpretação (o servidor ignora nome, modo e confirmação na versão semântica). */
function semantica(regra: RegraColuna): string {
  return JSON.stringify([
    regra.condicao, regra.descricao.trim(), regra.exclusoes, regra.exemplos, regra.prioridade,
    [...regra.origensPermitidas].sort(), regra.marcoAnalitico, regra.exigeRegistro,
  ]);
}

/**
 * "Mover nas colunas permitidas" só vale para rota calibrada e sem mudança de sentido neste rascunho:
 * mudar a regra invalida a calibração e o servidor recusaria a publicação.
 */
export function podeMoverSozinha(coluna: ColunaRascunho, configuracao: ConfiguracaoOrganizacaoTela | null): boolean {
  if (!configuracao || coluna.nova || !coluna.organizar || coluna.regra.condicao === 'retomar') return false;
  if (!configuracao.calibradas.includes(coluna.etapaId)) return false;
  const publicada = configuracao.regras.find((regra) => regra.etapaId === coluna.etapaId);
  return Boolean(publicada) && semantica(publicada as RegraColuna) === semantica(coluna.regra);
}

/** Avançar exige resolver o passo atual; colunas fora da organização não precisam ser configuradas. */
export function bloqueioDoPasso(passo: number, estado: EstadoAssistente): string | null {
  const organizadas = estado.colunas.filter((coluna) => coluna.organizar);
  if (passo === 1) {
    if (organizadas.length === 0) return 'Escolha ao menos uma coluna para organizar.';
    if (organizadas.length > LIMITES_ORGANIZACAO.colunas) return `Escolha no máximo ${LIMITES_ORGANIZACAO.colunas} colunas.`;
  }
  if (passo === 2) {
    const pendente = organizadas.find((coluna) => !regraPronta(coluna.regra) || !coluna.regra.confirmada);
    if (pendente) return `Confirme o significado da coluna “${pendente.nome}” para continuar.`;
  }
  return null;
}

/** Converte o estado do assistente no contrato único da publicação (RPC publicar_organizacao_kanban). */
export function paraRascunho(estado: EstadoAssistente, versaoEsperada: string): RascunhoOrganizacao {
  const organizadas = estado.colunas.filter((coluna) => coluna.organizar);
  // Origens só podem citar colunas existentes ou novas que também serão criadas nesta publicação.
  const novasCriadas = new Set(organizadas.filter((coluna) => coluna.nova).map((coluna) => coluna.etapaId));
  const novasDescartadas = new Set(estado.colunas.filter((coluna) => coluna.nova && !novasCriadas.has(coluna.etapaId)).map((coluna) => coluna.etapaId));
  return {
    origem: estado.origem,
    frequencia: estado.frequencia,
    versaoEsperada,
    novasColunas: organizadas.filter((coluna) => coluna.nova).map((coluna) => ({ idTemporario: coluna.etapaId, nome: coluna.nome })),
    regras: organizadas.map((coluna, indice) => ({
      ...coluna.regra,
      etapaId: coluna.etapaId,
      nome: coluna.nome,
      descricao: coluna.regra.descricao.trim(),
      prioridade: indice,
      origensPermitidas: coluna.regra.origensPermitidas.filter((id) => !novasDescartadas.has(id)),
      modo: estado.movimento === 'mover' && estado.automaticas.includes(coluna.etapaId) ? 'automatic' : 'suggestion',
      confirmada: true,
    })),
  };
}

/** Depois da publicação, troca os IDs provisórios pelos IDs reais devolvidos pelo servidor. */
export function aplicarIdsPublicados(colunas: ColunaRascunho[], mapa: Record<string, string>): ColunaRascunho[] {
  const trocar = (id: string) => mapa[id] ?? id;
  return colunas.map((coluna) => ({
    ...coluna,
    etapaId: trocar(coluna.etapaId),
    nova: coluna.nova && !mapa[coluna.etapaId],
    regra: { ...coluna.regra, etapaId: trocar(coluna.regra.etapaId), origensPermitidas: coluna.regra.origensPermitidas.map(trocar) },
  }));
}
